import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { SkillAttached, Course, SkillHeader } from '../types/microsoftGraph';

import { useAuth } from '../hooks/useAuth';
import { useSkillsCache } from '../context/skillsCacheContext';
import { api, API_ENDPOINTS } from '../config/api';

const SkillAssign: React.FC = () => {
    const { token } = useAuth();
    const { skills: cachedSkills, fetchSkills, loading: cacheLoading, error: cacheError, getSkillHeaders } = useSkillsCache();
    const [courses, setCourses] = useState<Course[]>([]);
    const [skills, setSkills] = useState<SkillHeader[]>([]);
    const [selectedCourseID, setSelectedCourseID] = useState<number | null>(null);
    const [assignedSkills, setAssignedSkills] = useState<SkillHeader[]>([]);
    const [unassignedSkills, setUnassignedSkills] = useState<SkillHeader[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [messageType, setMessageType] = useState<'success' | 'error'>('success');

    // Map of Skills_keyID -> SkillAttachedID
    const [assignedAttachments, setAssignedAttachments] = useState<Record<number, number>>({});

    const [dbDumpOpen, setDbDumpOpen] = useState(false);
    const [dbDumpContent, setDbDumpContent] = useState<string>('');
    const [dbDumpLoading, setDbDumpLoading] = useState<boolean>(false);
const navigate = useNavigate();

    useEffect(() => {
        if (token) {
            fetchInitialData();
            fetchSkills(); // ensure cache is loaded
        }
    }, [token, fetchSkills]); // also depend on fetchSkills

    const fetchInitialData = useCallback(async () => {
        setLoading(true);
        try {
            const coursesData = await api.get(API_ENDPOINTS.courses.list);
            setCourses(Array.isArray(coursesData) ? coursesData : []);

            // Get mapped SkillHeader items from cache helper
            const mappedSkills: SkillHeader[] = typeof getSkillHeaders === 'function' 
              ? getSkillHeaders() 
              : (cachedSkills || []).map((s: any) => ({ skillID: s.skillID ?? 0, skillName: s.skillName ?? '', skillLinkID: 0 }));
            setSkills(mappedSkills);
            setUnassignedSkills(mappedSkills);
        } catch (error) {
            console.error("Failed to fetch initial data", error);
            setMessage("Failed to load data. Please check the console.");
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    }, [token, cachedSkills, getSkillHeaders]); // updated deps

    // Keep skills and unassigned list in sync as cache updates
    useEffect(() => {
        // Re-map using cache helper if it exists
        const mapped = typeof getSkillHeaders === 'function' ? getSkillHeaders() : (cachedSkills || []).map((s: any) => ({ skillID: s.skillID ?? 0, skillName: s.skillName ?? '', skillLinkID: 0 }));
        setSkills(mapped);
        // Recompute unassigned based on current selection (if any)
        if (selectedCourseID !== null) {
            // re-run course selection update to recompute assigned/unassigned lists
            handleSelectCourse(selectedCourseID);
        } else {
            setUnassignedSkills(mapped);
            setAssignedSkills([]);
        }
    }, [cachedSkills]);

     const handleSelectCourse = async (courseID: number) => {
         setSelectedCourseID(courseID);
         setMessage('');
         
         try {
             const assignedData = await api.get(API_ENDPOINTS.skillAttached.get(courseID));
             const assignedArray = Array.isArray(assignedData) ? assignedData : [];

             // Build a list of assigned skill IDs (for the display) - from the UI payload
             const assignedSkillIDs = assignedArray.map((a: any) => {
                 const skillId = a.skillID ?? a.Skills_keyID ?? a.Skills_keyid ?? a.SkillsKeyID ?? 0;
                 return Number(skillId);
             });

             // Also fetch the actual attachment rows to extract SkillAttachedID for deletions
             const attachments = await fetchAttachmentsForCourse(courseID);
             const attachmentsMap: Record<number, number> = {};
             attachments.forEach((att: any) => {
                 const skillKey = Number(att.Skills_keyID ?? 0);
                 const attachId = Number(att.SkillAttachedID ?? 0);
                 if (skillKey && attachId) attachmentsMap[skillKey] = attachId;
             });

             setAssignedAttachments(attachmentsMap);

             const assigned = skills.filter(s => assignedSkillIDs.includes(s.skillID));
             const unassigned = skills.filter(s => !assignedSkillIDs.includes(s.skillID));
             
             setAssignedSkills(assigned);
             setUnassignedSkills(unassigned);
         } catch (error) {
             console.error("Failed to fetch assigned skills", error);
             setMessage("Failed to load assigned skills for this course.");
             setMessageType('error');
             setAssignedSkills([]);
             setUnassignedSkills(skills);
             setAssignedAttachments({});
         }
     };

    const handleAssignSkill = async (skill: SkillHeader) => {
        if (!selectedCourseID) return;

        try {
            const skillAtt: SkillAttached = {
                Skills_keyID: skill.skillID,
                CourseID: selectedCourseID,
                FileID: 0,
                Skill_name: skill.skillName,
                SkillattachedID: 0
            };

            const created = await api.post(API_ENDPOINTS.skillAttached.create, skillAtt);

            const createdAttachId = created?.SkillattachedID ?? created?.SkillAttachedID ?? created?.skillattachedID ?? created?.SkillAttachedId ?? 0;
            if (createdAttachId && skill.skillID) {
              setAssignedAttachments(prev => ({ ...prev, [skill.skillID]: Number(createdAttachId) }));
            }

            await handleSelectCourse(selectedCourseID);

            setMessage(`"${skill.skillName}" assigned successfully!`);
            setMessageType('success');
        } catch (error) {
            console.error('Error assigning skill:', error);
            setMessage('An error occurred while assigning the skill.');
            setMessageType('error');
        }
    };

    const handleUnassignSkill = async (skill: SkillHeader) => {
        if (!selectedCourseID) return;
        setMessage('');
        try {
            let attachedId = assignedAttachments[skill.skillID] ?? 0;

            if (!attachedId || attachedId === 0) {
                const attachments = await fetchAttachmentsForCourse(selectedCourseID);
                const matching = attachments.find((att: any) => Number(att.Skills_keyID) === Number(skill.skillID));
                if (matching && (matching.SkillAttachedID ?? matching.Skill_attachedID)) {
                    attachedId = Number(matching.SkillAttachedID ?? matching.Skill_attachedID);
                    setAssignedAttachments(prev => ({ ...prev, [skill.skillID]: attachedId }));
                }
            }

            const bodyPayload = { SkillAttachedID: attachedId ?? 0, Skills_keyID: skill.skillID, CourseID: selectedCourseID };

            let deleteSuccess = false;
            try {
                await api.delete(API_ENDPOINTS.skillAttached.delete, { body: JSON.stringify(bodyPayload) });
                deleteSuccess = true;
            } catch (err) {
                console.debug('DELETE by body threw:', err);
            }

            if (!deleteSuccess && attachedId && attachedId > 0) {
                try {
                    console.debug(`Attempting delete attached by id via path: ${attachedId}`);
                    await api.delete(`${API_ENDPOINTS.skillAttached.delete}/${attachedId}`);
                    deleteSuccess = true;
                } catch (err) {
                    console.debug('DELETE by path threw:', err);
                }
            }

            if (deleteSuccess) {
                await handleSelectCourse(selectedCourseID);

                setAssignedAttachments(prev => {
                    const copy = { ...prev };
                    delete copy[skill.skillID];
                    return copy;
                });

                setMessage(`"${skill.skillName}" unassigned successfully!`);
                setMessageType('success');
            } else {
                console.error('Failed to delete attachment, SkillAttachedID:', attachedId);
                setMessage('Failed to unassign skill.');
                setMessageType('error');
            }
        } catch (error) {
            console.error('Error unassigning skill:', error);
            setMessage('An error occurred while unassigning the skill.');
            setMessageType('error');
        }
    };

    const fetchAttachmentsForCourse = async (courseID: number) => {
        try {
            const data = await api.get(API_ENDPOINTS.skillAttached.byCourse(courseID));
            if (!Array.isArray(data)) return [];
            return data.map((a: any) => ({
                SkillAttachedID: a.SkillAttachedID ?? a.Skill_attachedID ?? a.SkillattachedID ?? a.SkillAttachedId ?? a.SkillattachmentID ?? 0,
                Skills_keyID: a.Skills_keyID ?? a.Skills_keyid ?? a.skillID ?? 0,
                CourseID: a.CourseID ?? courseID,
                ...a
            }));
        } catch (error) {
            console.error("fetchAttachmentsForCourse error:", error);
            return [];
        }
    };

    const fetchDbContent = useCallback(async () => {
        if (!token) {
            setMessage('Must be signed in to fetch DB dump.');
            setMessageType('error');
            return;
        }

        setDbDumpLoading(true);
        setDbDumpContent('');

        try {
            const candidateEndpoints = [
                API_ENDPOINTS.debug.dbDump,
                `${API_ENDPOINTS.debug.dbDump}/all`,
            ];

            for (const ep of candidateEndpoints) {
                try {
                    const json = await api.get(ep);
                    setDbDumpContent(JSON.stringify(json, null, 2));
                    setDbDumpOpen(true);
                    setDbDumpLoading(false);
                    return;
                } catch (err) {
                    console.debug('Endpoint failed:', ep, err);
                }
            }

            const coursesDump = await api.get(API_ENDPOINTS.courses.list);
            const coursesData = Array.isArray(coursesDump) ? coursesDump : [];

            const skillsDump = typeof getSkillHeaders === 'function' ? getSkillHeaders() : (cachedSkills || []);

            const attachmentsDump: Record<number, any[]> = {};
            for (const c of coursesData) {
                const courseID = Number(c.CourseID ?? c.courseID ?? 0);
                try {
                    const attachments = await fetchAttachmentsForCourse(courseID);
                    attachmentsDump[courseID] = attachments;
                } catch (err) {
                    attachmentsDump[courseID] = [];
                }
            }

            const dump = {
                timestamp: new Date().toISOString(),
                courses: coursesData,
                skills: skillsDump,
                attachments: attachmentsDump,
            };

            setDbDumpContent(JSON.stringify(dump, null, 2));
            setDbDumpOpen(true);
        } catch (error) {
            console.error('Error fetching DB content', error);
            setMessage('Failed to fetch DB content.');
            setMessageType('error');
        } finally {
            setDbDumpLoading(false);
        }
    }, [token, cachedSkills, getSkillHeaders]);

    // Utility: copy / download dump
    const copyDumpToClipboard = async () => {
        if (!dbDumpContent) return;
        try {
            await navigator.clipboard.writeText(dbDumpContent);
            setMessage('Database dump copied to clipboard');
            setMessageType('success');
        } catch (error) {
            console.error('copy to clipboard failed', error);
            setMessage('Failed to copy to clipboard');
            setMessageType('error');
        }
    };

    const downloadDumpFile = (filename = 'dbdump.json') => {
        if (!dbDumpContent) return;
        const blob = new Blob([dbDumpContent], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading || cacheLoading) {
        return <CircularProgress />;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                                <ArrowBackIcon />
                            </IconButton>
                <Typography variant="h4">
                    Skill Assignment Manager
                </Typography>

                <Button
                    variant="outlined"
                    onClick={fetchDbContent}
                    disabled={dbDumpLoading}
                    size="small"
                >
                    {dbDumpLoading ? 'Loading...' : 'Show DB'}
                </Button>
            </Box>

            {message && (
                <Alert severity={messageType} onClose={() => setMessage('')} sx={{ mb: 2 }}>
                    {message}
                </Alert>
            )}

            <Grid container spacing={2}>
                {/* Column 1: Courses */}
                <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 2, height: '600px', overflow: 'auto' }}>
                        <Typography variant="h6" gutterBottom>
                            Courses
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    {(courses || []).map((course) => (
                                        <TableRow
                                            key={course.CourseID}
                                            onClick={() => handleSelectCourse(course.CourseID)}
                                            sx={{
                                                cursor: 'pointer',
                                                backgroundColor: selectedCourseID === course.CourseID ? '#e3f2fd' : 'inherit',
                                                '&:hover': {
                                                    backgroundColor: '#f5f5f5',
                                                },
                                            }}
                                        >
                                            <TableCell>{course.Course_name}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* Column 2: Unassigned Skills */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, height: '600px', overflow: 'auto' }}>
                        <Typography variant="h6" gutterBottom>
                            Unassigned Skills
                        </Typography>
                        {selectedCourseID ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableBody>
                                        {(unassignedSkills || []).map((skill) => (
                                            <TableRow
                                                key={skill.skillID}
                                                hover
                                                onClick={() => handleAssignSkill(skill)}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <TableCell>{skill.skillName}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography color="textSecondary">
                                Select a course to view unassigned skills
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Column 3: Assigned Skills */}
                <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 2, height: '600px', overflow: 'auto' }}>
                        <Typography variant="h6" gutterBottom>
                            Assigned Skills
                        </Typography>
                        {selectedCourseID ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableBody>
                                        {(assignedSkills || []).map((skill) => (
                                            <TableRow
                                                key={skill.skillID}
                                                hover
                                                onClick={() => handleUnassignSkill(skill)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    backgroundColor: '#c8e6c9',
                                                    '&:hover': {
                                                        backgroundColor: '#a5d6a7',
                                                    },
                                                }}
                                            >
                                                <TableCell>{skill.skillName}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography color="textSecondary">
                                Select a course to view assigned skills
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Dialog to show DB content */}
            <Dialog open={dbDumpOpen} onClose={() => setDbDumpOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Database Dump</DialogTitle>
                <DialogContent dividers>
                    <pre style={{ maxHeight: '60vh', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                        {dbDumpContent || (dbDumpLoading ? 'Loading...' : 'No data')}
                    </pre>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { copyDumpToClipboard(); }} size="small">Copy</Button>
                    <Button onClick={() => { downloadDumpFile(); }} size="small">Download</Button>
                    <Button onClick={() => { setDbDumpOpen(false); }} size="small">Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SkillAssign;